import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Grid,
  IconButton,
  Stack,
  Switch as ToggleSwitch,
  TextField,
  Typography,
  useTokens,
} from '@barracuda-internal/bds-core';
import { Add, Close } from '@barracuda-internal/bds-core/dist/Icons/Core';
import { Sparkline } from 'common/components';
import AADRoleGuard from 'common/components/AADRoleGuard';
import AccountBackendOverridesSection from './AccountBackendOverridesSection';
import PromptLoggingCard from './PromptLoggingCard';
import ScanConfigCard from './ScanConfigCard';
import { actions } from './globalReducer';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const relativeTime = (iso) => {
  if (!iso) return null;
  const diffMs = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
};

const AccountCard = ({ account, onClick }) => {
  const tokens = useTokens();
  const accent = tokens.chartsColorSeries2Range100;
  const lastSeen = relativeTime(account.last_scan_at);
  return (
    <Card
      onClick={onClick}
      bdsType="staticSubtle"
      bdsBoxShadow="default"
      bdsBorderRadius="round"
      bdsPadding="spacious"
      sx={{
        cursor: 'pointer',
        transition: 'transform 120ms, box-shadow 120ms',
        '&:hover': { transform: 'translateY(-2px)' },
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.aliasesSpaceContainerDefault,
        minHeight: 180,
      }}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: tokens.aliasesSpaceComponentDense,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h4"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {account.name}
          </Typography>
          <Typography variant="code">{account.id}</Typography>
        </Box>
        <Chip bdsType="successNeutral" bdsSize="medium" label="Enabled" />
      </Stack>

      <Stack
        direction="row"
        sx={{ alignItems: 'center', gap: tokens.aliasesSpaceComponentDefault }}
      >
        <Sparkline data={account.scan_histogram_24h || []} width={150} height={36} />
        <Stack sx={{ gap: 0 }}>
          <Typography variant="h3" sx={{ color: accent }}>
            {(account.scan_count_24h ?? 0).toLocaleString()}
          </Typography>
          <Typography variant="captionSubtle">scans · last 24h</Typography>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        sx={{
          gap: tokens.aliasesSpaceComponentDefault,
          marginTop: 'auto',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="captionSubtle">
          {account.member_count ?? 0} members
        </Typography>
        <Typography variant="captionSubtle">·</Typography>
        <Typography variant="captionSubtle">
          {account.api_key_count ?? 0} API keys
        </Typography>
        {lastSeen && (
          <>
            <Typography variant="captionSubtle">·</Typography>
            <Typography variant="captionSubtle">Active {lastSeen}</Typography>
          </>
        )}
      </Stack>
    </Card>
  );
};

const AccountsContainer = () => {
  const tokens = useTokens();
  const dispatch = useDispatch();
  const { accounts, accountDetail, onboard } = useSelector(
    (state) => state.global
  );
  const authRoles = useSelector((state) => state.auth?.roles || []);
  // Derive a single highest-privilege role for PromptLoggingCard
  const currentRole = authRoles.includes('ADMIN')
    ? 'ADMIN'
    : authRoles.includes('OPS')
    ? 'OPS'
    : 'SUPPORT';

  const [onboardOpen, setOnboardOpen] = useState(false);
  const [onboardId, setOnboardId] = useState('');

  useEffect(() => {
    dispatch(actions.fetchAccounts());
  }, [dispatch]);

  useEffect(() => {
    if (onboard.lastCreated && onboardOpen) {
      setOnboardOpen(false);
      setOnboardId('');
      dispatch(actions.clearOnboardState());
    }
  }, [onboard.lastCreated, onboardOpen, dispatch]);

  const handleClose = () => dispatch(actions.closeAccountDetail());
  const handleToggle = (id, current) =>
    dispatch(actions.toggleAccount(id, !current));
  const handleOnboardSubmit = () => {
    const n = parseInt(onboardId, 10);
    if (!isNaN(n)) dispatch(actions.onboardAccount(n));
  };

  return (
    <Box sx={{ padding: tokens.aliasesSpaceContainerSpacious }}>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: tokens.aliasesSpaceContainerDefault,
        }}
      >
        <Stack sx={{ gap: tokens.aliasesSpaceComponentDense }}>
          <Typography variant="h2">Accounts</Typography>
          <Typography variant="captionSubtle">
            AuthDB accounts onboarded to Prompt Security. Add new ones via "Onboard
            account".
          </Typography>
        </Stack>
        <AADRoleGuard requires={['ADMIN', 'OPS']}>
          <Button
            bdsType="interactiveEmphasis"
            startIcon={<Add />}
            onClick={() => setOnboardOpen(true)}
          >
            Onboard account
          </Button>
        </AADRoleGuard>
      </Stack>

      {accounts.error && (
        <Typography
          variant="body1"
          sx={{
            color: tokens.aliasesColorTextError,
            marginBottom: tokens.aliasesSpaceContainerDefault,
          }}
        >
          {accounts.error}
        </Typography>
      )}

      {accounts.isLoading && accounts.items.length === 0 ? (
        <Typography variant="body1">Loading…</Typography>
      ) : accounts.items.length === 0 ? (
        <Box
          sx={{
            padding: tokens.aliasesSpaceContainerSpacious,
            textAlign: 'center',
            border: `1px dashed ${tokens.aliasesColorBorderStaticSubtle}`,
            borderRadius: 1,
          }}
        >
          <Typography
            variant="body1"
            sx={{ marginBottom: tokens.aliasesSpaceComponentDense }}
          >
            No accounts onboarded yet.
          </Typography>
          <Typography variant="captionSubtle">
            Click "Onboard account" and enter an AuthDB account ID to add your first one.
          </Typography>
        </Box>
      ) : (
        <Grid container sx={{ gap: tokens.aliasesSpaceComponentSpacious }}>
          {accounts.items.map((a) => (
            <Grid key={a.id} item xs={12} sm={6} md={4} lg={3}>
              <AccountCard
                account={a}
                onClick={() => dispatch(actions.fetchAccountDetail(a.id))}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Onboarding dialog */}
      <Dialog
        open={onboardOpen}
        onClose={() => setOnboardOpen(false)}
        bdsDialogWidth="small"
      >
        <DialogTitle>Onboard a new account</DialogTitle>
        <DialogContent>
          <Stack sx={{ gap: tokens.aliasesSpaceContainerDefault }}>
            <Typography variant="captionSubtle">
              Enter the AuthDB account ID. We'll fetch the account from AuthDB
              and enable Prompt Security for it. If the account already exists
              locally and is disabled, this re-enables it.
            </Typography>
            <TextField
              label="AuthDB account ID"
              type="number"
              value={onboardId}
              onChange={(e) => setOnboardId(e.target.value)}
              fullWidth
              autoFocus
              disabled={onboard.isLoading}
            />
            {onboard.error && (
              <Typography
                variant="body1"
                sx={{ color: tokens.aliasesColorTextError }}
              >
                {onboard.error}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            bdsType="interactiveNeutral"
            variant="outlined"
            onClick={() => setOnboardOpen(false)}
            disabled={onboard.isLoading}
          >
            Cancel
          </Button>
          <Button
            bdsType="interactiveEmphasis"
            onClick={handleOnboardSubmit}
            disabled={onboard.isLoading || !onboardId}
          >
            {onboard.isLoading ? 'Onboarding…' : 'Onboard'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail drawer */}
      <Drawer
        anchor="right"
        open={Boolean(accountDetail.data || accountDetail.isLoading)}
        onClose={handleClose}
        PaperProps={{ sx: { width: 480 } }}
      >
        <Box sx={{ padding: tokens.aliasesSpaceContainerSpacious }}>
          <Stack
            direction="row"
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: tokens.aliasesSpaceContainerDefault,
            }}
          >
            <Typography variant="h3">Account details</Typography>
            <IconButton bdsType="interactiveSubtle" onClick={handleClose}>
              <Close />
            </IconButton>
          </Stack>

          {accountDetail.isLoading && (
            <Typography variant="body1">Loading…</Typography>
          )}
          {accountDetail.error && (
            <Typography
              variant="body1"
              sx={{ color: tokens.aliasesColorTextError }}
            >
              {accountDetail.error}
            </Typography>
          )}
          {accountDetail.data && (
            <Stack sx={{ gap: tokens.aliasesSpaceContainerDefault }}>
              <Box>
                <Typography variant="captionSubtle">Name</Typography>
                <Typography variant="body1">{accountDetail.data.name}</Typography>
              </Box>
              <Box>
                <Typography variant="captionSubtle">AuthDB ID</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {accountDetail.data.id}
                </Typography>
              </Box>

              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  gap: tokens.aliasesSpaceComponentDefault,
                  padding: tokens.aliasesSpaceContainerDefault,
                  borderRadius: 1,
                  backgroundColor: tokens.aliasesColorBgStaticContainerSubtle,
                }}
              >
                <Stack sx={{ flex: 1, gap: tokens.aliasesSpaceComponentDense }}>
                  <Typography variant="body1Bold">Prompt Security enabled</Typography>
                  <Typography variant="captionSubtle">
                    Disabling removes this account from the active list. You can
                    re-enable it later via "Onboard account".
                  </Typography>
                </Stack>
                <AADRoleGuard requires="ADMIN">
                  <ToggleSwitch
                    checked={accountDetail.data.ai_guardian_enabled}
                    onChange={() =>
                      handleToggle(
                        accountDetail.data.id,
                        accountDetail.data.ai_guardian_enabled
                      )
                    }
                  />
                </AADRoleGuard>
              </Stack>

              <Stack
                direction="row"
                sx={{ gap: tokens.aliasesSpaceComponentSpacious }}
              >
                <Box>
                  <Typography variant="captionSubtle">Members</Typography>
                  <Typography variant="body1">
                    {accountDetail.data.member_count}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="captionSubtle">API keys</Typography>
                  <Typography variant="body1">
                    {accountDetail.data.api_key_count}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="captionSubtle">Scans (24h)</Typography>
                  <Typography variant="body1">
                    {accountDetail.data.scan_count_24h}
                  </Typography>
                </Box>
              </Stack>

              <Box>
                <Typography
                  variant="captionSubtle"
                  sx={{ marginBottom: tokens.aliasesSpaceComponentDense, display: 'block' }}
                >
                  Members ({accountDetail.data.members?.length ?? 0})
                </Typography>
                {accountDetail.data.members?.length === 0 ? (
                  <Typography variant="captionSubtle">
                    No members yet — they'll appear once they log in.
                  </Typography>
                ) : (
                  <Stack sx={{ gap: tokens.aliasesSpaceComponentDense }}>
                    {accountDetail.data.members?.map((m) => (
                      <Stack
                        key={m.user_id}
                        direction="row"
                        sx={{
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: tokens.aliasesSpaceComponentDefault,
                        }}
                      >
                        <Box>
                          <Typography variant="body1">{m.username}</Typography>
                          <Typography variant="captionSubtle">{m.name}</Typography>
                        </Box>
                        <Chip
                          label={m.role.replace('account_', '')}
                          bdsType={m.role === 'account_admin'
                            ? 'staticEmphasis'
                            : 'staticNeutral'}
                          bdsSize="medium"
                        />
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Box>

              <AccountBackendOverridesSection accountId={accountDetail.data.id} />

              {/* Scan config at customer level */}
              <ScanConfigCard
                accountId={accountDetail.data.id}
                initialConfig={accountDetail.data.scan_config ?? {}}
                role={currentRole}
                onSave={(config) =>
                  dispatch(actions.updateScanConfig(accountDetail.data.id, config))
                }
              />

              {/* Prompt logging per API key */}
              {(accountDetail.data.api_keys ?? []).length === 0 ? (
                <Typography variant="captionSubtle">
                  No API keys — prompt logging is configured per API key.
                </Typography>
              ) : (
                (accountDetail.data.api_keys ?? []).map((k) => (
                  <PromptLoggingCard
                    key={k.id}
                    accountId={k.id}
                    apiKeyName={k.name}
                    initialEnabled={k.enable_prompt_logging}
                    role={currentRole}
                    onToggle={(enabled) =>
                      dispatch(actions.togglePromptLogging(k.id, enabled))
                    }
                  />
                ))
              )}

              <Box>
                <Typography
                  variant="captionSubtle"
                  sx={{ marginBottom: tokens.aliasesSpaceComponentDense, display: 'block' }}
                >
                  Recent scans
                </Typography>
                {accountDetail.data.recent_scans?.length === 0 ? (
                  <Typography variant="captionSubtle">No scans yet.</Typography>
                ) : (
                  <Stack sx={{ gap: tokens.aliasesSpaceComponentDense }}>
                    {accountDetail.data.recent_scans?.map((s) => (
                      <Stack
                        key={s.correlation_id}
                        direction="row"
                        sx={{ justifyContent: 'space-between' }}
                      >
                        <Typography
                          variant="captionSubtle"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {s.correlation_id.substring(0, 12)}…
                        </Typography>
                        <Typography variant="captionSubtle">
                          {s.status} · {fmtDate(s.started_at)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default AccountsContainer;
